import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Users, Plus, Search, Upload, Download, Trash2, Edit3, ArrowLeft,
  Phone, X, CheckCircle, AlertTriangle, Loader2, UserPlus,
  BanIcon, FileSpreadsheet, MoreVertical, Eye, ChevronDown,
} from "lucide-react";
import {
  AppButton, EmptyState, ConfirmModal, Paginator, ViewToggle,
  ActionMenu, ActionMenuItem, ActionMenuDivider,
} from "../ui";
import { Form, Modal, Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import Shimmer from "../Common/Shimmer";
import moment from "moment";
import "./contact-groups.scss";

/* ── Constants ───────────────────────────────────────────────────────────────
 *
 * IMPORT_CHUNK_SIZE must stay at or below MAX_IMPORT_ROWS in
 * vc_be/controllers/whatsappContactController.js (5000) — the endpoint rejects a
 * larger chunk with 413 rather than truncating it.
 */
const IMPORT_CHUNK_SIZE = 2000;

/** Refuse a file this size in the browser rather than discovering row 200,001
 *  is the one that runs the tab out of memory. */
const MAX_IMPORT_TOTAL_ROWS = 200000;

const GROUP_COLORS = [
  // Group swatches the user PICKS FROM and we store on the group, so these are
  // data rather than styling — a stored `hsl(var(--x))` would break the moment
  // the palette moved under it. They stay literals for that reason; the first is
  // WhatsApp's own green, which is an external brand and never --primary.
  "#25D366", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", "#14b8a6", "#ec4899", "#64748b",
];

const STATUS_META = {
  active: { label: "Active", cls: "active" },
  unsubscribed: { label: "Unsubscribed", cls: "unsub" },
  invalid: { label: "Invalid", cls: "invalid" },
};

/* Sorted in the browser rather than by the endpoint: the list is one institute's
 * groups (tens, not thousands) and it is already fully in memory, so a round trip
 * per sort change would only add latency. "Latest" is `createdAt`, not the
 * server's `updatedAt` ordering — the card shows a created date, and a sort that
 * disagrees with the date printed on the thing being sorted reads as a bug. */
const SORT_OPTIONS = [
  { value: "latest", label: "Latest" },
  { value: "oldest", label: "Oldest" },
  { value: "name", label: "Name (A–Z)" },
  { value: "contacts", label: "Most contacts" },
];

/**
 * The dot on each group card. Green is "nothing wrong here", not "has contacts" —
 * a group with contacts but nothing sendable is the one state worth flagging,
 * because it looks fine in every other number on the card and sends to nobody.
 */
const groupHealth = (g) => {
  const total = g.contactCount || 0;
  const active = g.activeCount || 0;
  if (total > 0 && active === 0) {
    return { tone: "warn", label: "No sendable contacts — every number is unsubscribed or invalid" };
  }
  return {
    tone: "ok",
    label: total === 0
      ? "Empty — no numbers imported yet"
      : `${active.toLocaleString()} sendable contact${active !== 1 ? "s" : ""}`,
  };
};

/**
 * Download a group as CSV.
 *
 * The export route sits behind authMiddleware, which reads the Authorization
 * header — a bare `<a href>` cannot set one, so the CSV is fetched through
 * apiClient and saved as a blob rather than linked to directly.
 */
async function downloadGroupCsv(group, status, showToast) {
  try {
    const { data } = await apiClient.get("/whatsapp/contacts/export", {
      params: { groupId: group._id, ...(status ? { status } : {}) },
      responseType: "blob",
    });
    const href = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = href;
    a.download = `${group.name.replace(/[^\w-]+/g, "_")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch {
    showToast("Export failed", "error");
  }
}

/* ── CSV / paste parsing ────────────────────────────────────────────────────
 *
 * Deliberately hand-rolled rather than pulling in a CSV library: the accepted
 * shape is three columns of short, quoted-at-most text, and the parser below
 * handles the one thing that actually breaks naive `split(',')` — a quoted field
 * containing a comma, which is exactly what a course name like
 * "Data Science, Advanced" produces from Excel.
 */
function parseDelimitedLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }   // escaped quote
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === "," || ch === "\t" || ch === ";") { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

const looksLikeHeader = (cells) =>
  cells.some((c) => /^(phone|mobile|number|name|course|contact)$/i.test(c.trim()));

/**
 * Turn pasted text or a CSV file into `{ phone, name, course }` rows.
 *
 * Column order is inferred from the header row when there is one, so an export
 * from another tool with `Name,Phone,Course` is not silently imported with every
 * name as a phone number. Without a header, the first column is the phone —
 * stated in the UI, because guessing is worse than a rule.
 */
function parseContactRows(text) {
  const lines = String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], skipped: 0 };

  let idx = { phone: 0, name: 1, course: 2 };
  let start = 0;

  const firstCells = parseDelimitedLine(lines[0]);
  if (looksLikeHeader(firstCells)) {
    start = 1;
    const find = (re) => firstCells.findIndex((c) => re.test(c.trim()));
    const p = find(/^(phone|mobile|number)$/i);
    const n = find(/^(name|contact)$/i);
    const c = find(/^course$/i);
    idx = { phone: p >= 0 ? p : 0, name: n, course: c };
  }

  const rows = [];
  let skipped = 0;
  for (let i = start; i < lines.length; i++) {
    const cells = parseDelimitedLine(lines[i]);
    const phone = (cells[idx.phone] || "").replace(/[^\d+]/g, "");
    // 10 digits is the shortest thing that can be a real number here; anything
    // less is a stray cell, a total row, or a note someone left in the sheet.
    if (phone.replace(/\D/g, "").length < 10) { skipped++; continue; }
    rows.push({
      phone,
      name: idx.name >= 0 ? (cells[idx.name] || "") : "",
      course: idx.course >= 0 ? (cells[idx.course] || "") : "",
    });
  }
  return { rows, skipped };
}

/* ══════════════════════════════════════════════════════════════════════════
   Contacts Tab (main export)
   ══════════════════════════════════════════════════════════════════════════ */
const ContactGroupsTab = ({ showToast }) => {
  const canManage = can("whatsapp.manage");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("latest");
  const [view, setView] = useState("grid");
  const [activeGroup, setActiveGroup] = useState(null);

  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importTarget, setImportTarget] = useState(null);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/whatsapp/contact-groups", {
        params: search.trim() ? { search: search.trim() } : {},
      });
      setGroups(data.groups || []);
    } catch { showToast("Failed to load contact groups", "error"); }
    setLoading(false);
  }, [search, showToast]);

  useEffect(() => {
    const t = setTimeout(fetchGroups, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchGroups, search]);

  const handleDeleteGroup = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await apiClient.delete(`/whatsapp/contact-groups/${deleteTarget._id}`);
      showToast(data.message || "Group deleted");
      setDeleteTarget(null);
      if (activeGroup?._id === deleteTarget._id) setActiveGroup(null);
      fetchGroups();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to delete group", "error");
    }
    setDeleting(false);
  };

  const totalContacts = useMemo(
    () => groups.reduce((sum, g) => sum + (g.contactCount || 0), 0), [groups],
  );

  const sortedGroups = useMemo(() => {
    const at = (g) => new Date(g.createdAt || 0).getTime();
    const list = [...groups];
    switch (sort) {
      case "oldest": return list.sort((a, b) => at(a) - at(b));
      case "name": return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      case "contacts": return list.sort((a, b) => (b.contactCount || 0) - (a.contactCount || 0));
      default: return list.sort((a, b) => at(b) - at(a));
    }
  }, [groups, sort]);

  // ── Group detail view ──
  if (activeGroup) {
    return (
      <GroupDetail
        group={activeGroup}
        onBack={() => { setActiveGroup(null); fetchGroups(); }}
        onImport={() => setImportTarget(activeGroup)}
        showToast={showToast}
        allGroups={groups}
        importTarget={importTarget}
        onCloseImport={(refresh) => {
          setImportTarget(null);
          if (refresh) fetchGroups();
        }}
      />
    );
  }

  return (
    <div className="wa-contacts">
      <div className="wa-contacts__header">
        <div>
          <h3 className="wa-contacts__title">
            Contact Groups
            {!loading && <span className="wa-contacts__title-count">{groups.length}</span>}
          </h3>
          <p className="wa-contacts__subtitle">
            Save your phone numbers into reusable groups, then pick a group when you
            broadcast or schedule — instead of pasting numbers every time.
          </p>
        </div>
        {canManage && (
          <AppButton
            className="wa-contacts__new"
            onClick={() => { setEditingGroup(null); setShowGroupForm(true); }}
          >
            <Plus size={16} /> New Group
          </AppButton>
        )}
      </div>

      <div className="wa-contacts__toolbar">
        <div className="wa-contacts__search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search groups…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="wa-contacts__search-clear" onClick={() => setSearch("")} title="Clear">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="wa-contacts__toolbar-right">
          <label className="wa-cg-sort">
            <span className="wa-cg-sort__label">Sort by:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort groups">
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="wa-cg-sort__chev" />
          </label>
          <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {!loading && groups.length > 0 && (
        <div className="wa-contacts__count">
          {groups.length} group{groups.length !== 1 ? "s" : ""} · {totalContacts.toLocaleString()} contacts
        </div>
      )}

      {loading ? (
        // Mirrors the grid it is standing in for, so the cards do not jump into
        // a different shape when they arrive.
        <div className={view === "grid" ? "wa-contacts__grid" : "wa-contacts__list"}>
          {Array.from({ length: view === "grid" ? 4 : 6 }).map((_, i) => (
            <div
              key={i}
              className="shimmer wa-cg-skeleton"
              style={{ height: view === "grid" ? 196 : 58 }}
            />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Users size={40} />}
          title={search ? "No groups match that search" : "No Contact Groups Yet"}
          description={
            search
              ? "Try a different name."
              : "Create a group, import your numbers once, and reuse it for every broadcast."
          }
        />
      ) : (
        <>
          <div className={view === "grid" ? "wa-contacts__grid" : "wa-contacts__list"}>
            {sortedGroups.map((g) => {
              const handlers = {
                onOpen: () => setActiveGroup(g),
                onEdit: canManage ? () => { setEditingGroup(g); setShowGroupForm(true); } : undefined,
                onDelete: canManage ? () => setDeleteTarget(g) : undefined,
                onImport: canManage ? () => setImportTarget(g) : undefined,
                onExport: () => downloadGroupCsv(g, "", showToast),
              };
              return view === "grid"
                ? <GroupCard key={g._id} group={g} {...handlers} />
                : <GroupRow key={g._id} group={g} {...handlers} />;
            })}
          </div>
          <GroupsPromo />
        </>
      )}

      {showGroupForm && (
        <GroupFormModal
          group={editingGroup}
          onClose={(refresh) => {
            setShowGroupForm(false);
            setEditingGroup(null);
            if (refresh) fetchGroups();
          }}
          showToast={showToast}
        />
      )}

      {importTarget && !activeGroup && (
        <ImportModal
          group={importTarget}
          onClose={(refresh) => { setImportTarget(null); if (refresh) fetchGroups(); }}
          showToast={showToast}
        />
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={handleDeleteGroup}
        title="Delete Contact Group"
        variant="danger"
        confirmText="Delete Group"
        loading={deleting}
        message={
          <div>
            <p>Delete <strong>{deleteTarget?.name}</strong>?</p>
            <div className="wa-alert wa-alert--info" style={{ marginTop: 8 }}>
              <CheckCircle size={14} />
              Contacts that also belong to another group are kept. Only numbers that
              exist nowhere else are removed — and unsubscribed numbers are always
              kept, so they stay suppressed from future sends.
            </div>
          </div>
        }
      />
    </div>
  );
};

/* ── Shared card/row pieces ─────────────────────────────────────────────── */

/** The kebab. Carries everything the visible buttons do plus Export, which has
 *  no room for a button of its own and otherwise only exists inside a group. */
const GroupMenu = ({ onOpen, onImport, onExport, onEdit, onDelete }) => (
  <ActionMenu trigger={<MoreVertical size={16} />}>
    <ActionMenuItem onClick={onOpen}><Eye size={14} /> View contacts</ActionMenuItem>
    {onImport && <ActionMenuItem onClick={onImport}><Upload size={14} /> Import numbers</ActionMenuItem>}
    <ActionMenuItem onClick={onExport}><Download size={14} /> Export CSV</ActionMenuItem>
    {onEdit && <ActionMenuItem onClick={onEdit}><Edit3 size={14} /> Edit group</ActionMenuItem>}
    {onDelete && (
      <>
        <ActionMenuDivider />
        <ActionMenuItem danger onClick={onDelete}><Trash2 size={14} /> Delete group</ActionMenuItem>
      </>
    )}
  </ActionMenu>
);

const GroupCreated = ({ group }) => (
  <>Created on {moment(group.createdAt).format("DD MMM, YYYY")} • {moment(group.createdAt).format("hh:mm A")}</>
);

/* ── Group Card (grid view) ─────────────────────────────────────────────── */
const GroupCard = ({ group, onOpen, onEdit, onDelete, onImport, onExport }) => {
  const inactive = (group.contactCount || 0) - (group.activeCount || 0);
  const health = groupHealth(group);
  return (
    <div className="wa-cg-card" style={{ "--cg-color": group.color || "#25D366" }}>
      <div className="wa-cg-card__controls">
        <span className={`wa-cg-dot wa-cg-dot--${health.tone}`} title={health.label} />
        <GroupMenu
          onOpen={onOpen} onImport={onImport} onExport={onExport}
          onEdit={onEdit} onDelete={onDelete}
        />
      </div>

      <button className="wa-cg-card__main" onClick={onOpen}>
        <div className="wa-cg-card__top">
          <span className="wa-cg-card__avatar"><Users size={22} /></span>
          <span className="wa-cg-card__id">
            <span className="wa-cg-card__name" title={group.name}>{group.name}</span>
            <span className="wa-cg-card__badge">Custom Group</span>
          </span>
        </div>
        {group.description && <p className="wa-cg-card__desc">{group.description}</p>}
        <div className="wa-cg-card__count">
          <strong>{(group.contactCount || 0).toLocaleString()}</strong>
          <span>
            contacts
            {inactive > 0 && (
              // Surfaced rather than hidden: the gap between "how many numbers I
              // uploaded" and "how many will receive anything" is the number an
              // operator is actually surprised by.
              <em> · {inactive.toLocaleString()} excluded</em>
            )}
          </span>
        </div>
      </button>

      <div className="wa-cg-card__footer">
        <div className="wa-cg-card__meta">
          <span><GroupCreated group={group} /></span>
          {group.lastUsedAt && <span>Last used {moment(group.lastUsedAt).fromNow()}</span>}
        </div>
        <div className="wa-cg-card__actions">
          {onImport && <button onClick={onImport}><Upload size={14} /> Upload</button>}
          {onEdit && <button onClick={onEdit}><Edit3 size={14} /> Edit</button>}
          <button onClick={onOpen}><Eye size={14} /> View</button>
          {onDelete && <button className="danger" onClick={onDelete}><Trash2 size={14} /> Delete</button>}
        </div>
      </div>
    </div>
  );
};

/* ── Group Row (list view) ──────────────────────────────────────────────── */
const GroupRow = ({ group, onOpen, onEdit, onDelete, onImport, onExport }) => {
  const inactive = (group.contactCount || 0) - (group.activeCount || 0);
  const health = groupHealth(group);
  return (
    <div className="wa-cg-row" style={{ "--cg-color": group.color || "#25D366" }}>
      <button className="wa-cg-row__main" onClick={onOpen}>
        <span className="wa-cg-card__avatar wa-cg-card__avatar--sm"><Users size={16} /></span>
        <span className="wa-cg-row__id">
          <span className="wa-cg-card__name" title={group.name}>{group.name}</span>
          <span className="wa-cg-card__badge">Custom Group</span>
        </span>
        <span className="wa-cg-row__count">
          <strong>{(group.contactCount || 0).toLocaleString()}</strong> contacts
          {inactive > 0 && <em> · {inactive.toLocaleString()} excluded</em>}
        </span>
        <span className="wa-cg-row__date"><GroupCreated group={group} /></span>
      </button>
      <div className="wa-cg-row__controls">
        <span className={`wa-cg-dot wa-cg-dot--${health.tone}`} title={health.label} />
        <GroupMenu
          onOpen={onOpen} onImport={onImport} onExport={onExport}
          onEdit={onEdit} onDelete={onDelete}
        />
      </div>
    </div>
  );
};

/* ── Promo / explainer strip ────────────────────────────────────────────── */
const GroupsPromo = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`wa-cg-promo ${open ? "is-open" : ""}`}>
      <div className="wa-cg-promo__row">
        <span className="wa-cg-promo__icon"><Users size={22} /></span>
        <div className="wa-cg-promo__text">
          <h4>Organize. Segment. Engage.</h4>
          <p>
            Create smart contact groups to target the right audience with personalized
            messages and campaigns.
          </p>
        </div>
        {/* Expands in place rather than linking out — there is no docs site to
            point at, and a "Learn More" that opens nothing is worse than none. */}
        <button
          className="wa-cg-promo__cta"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {open ? "Show Less" : "Learn More"} <ChevronDown size={15} />
        </button>
      </div>
      {open && (
        <ol className="wa-cg-promo__steps">
          <li>
            <strong>Create a group</strong> for an audience you message repeatedly —
            a campaign list, walk-in enquiries, one batch's parents.
          </li>
          <li>
            <strong>Import the numbers once.</strong> Paste them or upload a CSV; the
            same number in two groups stays a single contact, so opt-outs apply everywhere.
          </li>
          <li>
            <strong>Pick the group</strong> in Broadcast or Scheduled instead of pasting
            numbers again. Unsubscribed and invalid numbers are excluded automatically.
          </li>
        </ol>
      )}
    </div>
  );
};

/* ── Group create/edit modal ────────────────────────────────────────────── */
const GroupFormModal = ({ group, onClose, showToast }) => {
  const [name, setName] = useState(group?.name || "");
  const [description, setDescription] = useState(group?.description || "");
  const [color, setColor] = useState(group?.color || GROUP_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), color };
      if (group) await apiClient.put(`/whatsapp/contact-groups/${group._id}`, payload);
      else await apiClient.post("/whatsapp/contact-groups", payload);
      showToast(group ? "Group updated" : "Group created");
      onClose(true);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save group", "error");
    }
    setSaving(false);
  };

  return (
    <Modal show centered onHide={() => !saving && onClose(false)}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
          {group ? "Edit Group" : "New Contact Group"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Group name <span className="wa-required">*</span></Form.Label>
          <Form.Control
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Diwali Campaign 2026, Walk-in Enquiries"
            maxLength={120}
            onKeyDown={(e) => e.key === "Enter" && name.trim() && save()}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Description <span className="wa-optional">(optional)</span></Form.Label>
          <Form.Control
            as="textarea" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this list for?" maxLength={500}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Colour</Form.Label>
          <div className="wa-cg-colors">
            {GROUP_COLORS.map((c) => (
              <button
                key={c} type="button"
                className={`wa-cg-color ${color === c ? "wa-cg-color--active" : ""}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancel</AppButton>
        <AppButton onClick={save} disabled={!name.trim() || saving}>
          {saving ? <Loader2 size={14} className="wa-spin" /> : null} {group ? "Save" : "Create Group"}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   Group detail — the contact table
   ══════════════════════════════════════════════════════════════════════════ */
const GroupDetail = ({ group, onBack, onImport, showToast, allGroups, importTarget, onCloseImport }) => {
  const canManage = can("whatsapp.manage");
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editingContact, setEditingContact] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [groupStats, setGroupStats] = useState(group);
  const limit = 50;

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { groupId: group._id, page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const { data } = await apiClient.get("/whatsapp/contacts", { params });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
    } catch { showToast("Failed to load contacts", "error"); }
    setLoading(false);
  }, [group._id, page, search, statusFilter, showToast]);

  useEffect(() => {
    const t = setTimeout(fetchContacts, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchContacts, search]);

  const refreshStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/whatsapp/contact-groups");
      const fresh = (data.groups || []).find((g) => g._id === group._id);
      if (fresh) setGroupStats(fresh);
    } catch { /* the header count is cosmetic */ }
  }, [group._id]);

  const afterMutation = useCallback(() => {
    setSelected(new Set());
    fetchContacts();
    refreshStats();
  }, [fetchContacts, refreshStats]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allOnPageSelected = contacts.length > 0 && contacts.every((c) => selected.has(c._id));
  const toggleAllOnPage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allOnPageSelected) contacts.forEach((c) => next.delete(c._id));
    else contacts.forEach((c) => next.add(c._id));
    return next;
  });

  const runBulk = async ({ action, targetGroupId, allInGroup }) => {
    setBulkBusy(true);
    try {
      const payload = allInGroup
        ? { action, groupId: group._id, allInGroup: true, targetGroupId }
        : { action, contactIds: [...selected], targetGroupId, groupId: group._id };
      const { data } = await apiClient.post("/whatsapp/contacts/bulk", payload);
      showToast(`${data.affected} contact${data.affected !== 1 ? "s" : ""} updated`);
      setConfirmBulk(null);
      afterMutation();
    } catch (err) {
      showToast(err?.response?.data?.message || "Bulk action failed", "error");
    }
    setBulkBusy(false);
  };

  const [exporting, setExporting] = useState(false);
  // Exports what is filtered on screen, not the whole group — the status filter
  // is part of what the operator thinks they are looking at.
  const exportCsv = async () => {
    setExporting(true);
    await downloadGroupCsv(group, statusFilter, showToast);
    setExporting(false);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="wa-contacts">
      <div className="wa-cg-detail__header">
        <button className="wa-cg-detail__back" onClick={onBack}>
          <ArrowLeft size={16} /> All groups
        </button>
        <div className="wa-cg-detail__title-row">
          <span className="wa-cg-swatch" style={{ "--cg-color": group.color || "#25D366" }} />
          <h3 className="wa-contacts__title">{group.name}</h3>
          <span className="wa-cg-detail__badge">
            {(groupStats.activeCount || 0).toLocaleString()} sendable
            {" / "}
            {(groupStats.contactCount || 0).toLocaleString()} total
          </span>
        </div>
        {group.description && <p className="wa-contacts__subtitle">{group.description}</p>}
      </div>

      <div className="wa-cg-detail__toolbar">
        <div className="wa-contacts__search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search name, number or course…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="wa-contacts__search-clear" onClick={() => setSearch("")}><X size={14} /></button>
          )}
        </div>
        <Form.Select
          size="sm" className="wa-cg-detail__filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="invalid">Invalid</option>
        </Form.Select>
        <div className="wa-cg-detail__actions">
          {canManage && (
            <>
              <AppButton size="sm" variant="outline" onClick={() => setShowAdd(true)}>
                <UserPlus size={14} /> Add
              </AppButton>
              <AppButton size="sm" variant="outline" onClick={onImport}>
                <Upload size={14} /> Import
              </AppButton>
            </>
          )}
          <AppButton size="sm" variant="outline" onClick={exportCsv} disabled={exporting}>
            {exporting ? <Loader2 size={14} className="wa-spin" /> : <Download size={14} />} Export
          </AppButton>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="wa-cg-bulkbar">
          <span><strong>{selected.size}</strong> selected</span>
          <button onClick={() => setConfirmBulk({ action: "unsubscribe" })}>
            <BanIcon size={13} /> Unsubscribe
          </button>
          <button onClick={() => setConfirmBulk({ action: "reactivate" })}>
            <CheckCircle size={13} /> Reactivate
          </button>
          <button onClick={() => setConfirmBulk({ action: "copy" })}>
            <Users size={13} /> Copy to group
          </button>
          <button onClick={() => setConfirmBulk({ action: "remove_from_group" })}>
            <X size={13} /> Remove from group
          </button>
          <button className="danger" onClick={() => setConfirmBulk({ action: "delete" })}>
            <Trash2 size={13} /> Delete
          </button>
          <button className="wa-cg-bulkbar__clear" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {loading ? (
        <Shimmer height={40} count={8} />
      ) : contacts.length === 0 ? (
        <EmptyState
          icon={<Phone size={36} />}
          title={search || statusFilter ? "No contacts match" : "This group is empty"}
          description={
            search || statusFilter
              ? "Try clearing the search or filter."
              : "Import a CSV or paste your numbers to get started."
          }
        />
      ) : (
        <>
          <div className="wa-cg-table-wrap">
            <table className="wa-cg-table">
              <thead>
                <tr>
                  {canManage && (
                    <th className="wa-cg-table__check">
                      <input type="checkbox" checked={allOnPageSelected} onChange={toggleAllOnPage} />
                    </th>
                  )}
                  <th>Phone</th>
                  <th>Name</th>
                  <th>Course</th>
                  <th>Status</th>
                  <th>Groups</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c._id} className={selected.has(c._id) ? "is-selected" : ""}>
                    {canManage && (
                      <td className="wa-cg-table__check">
                        <input type="checkbox" checked={selected.has(c._id)} onChange={() => toggle(c._id)} />
                      </td>
                    )}
                    <td className="wa-cg-table__phone">{c.phone}</td>
                    <td>{c.name || <span className="wa-cg-table__dim">—</span>}</td>
                    <td>{c.course || <span className="wa-cg-table__dim">—</span>}</td>
                    <td>
                      <span className={`wa-cg-status wa-cg-status--${STATUS_META[c.status]?.cls || "active"}`}>
                        {STATUS_META[c.status]?.label || c.status}
                      </span>
                    </td>
                    <td className="wa-cg-table__groups">
                      {(c.groupIds || []).map((g) => (
                        <span key={g._id} className="wa-cg-chip" style={{ "--cg-color": g.color || "#25D366" }}>
                          {g.name}
                        </span>
                      ))}
                    </td>
                    {canManage && (
                      <td className="wa-cg-table__row-actions">
                        <button onClick={() => setEditingContact(c)} title="Edit"><Edit3 size={13} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginator
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={limit}
            onChange={setPage}
            infoLabel="contacts"
          />
        </>
      )}

      {(showAdd || editingContact) && (
        <ContactFormModal
          contact={editingContact}
          groupId={group._id}
          onClose={(refresh) => {
            setShowAdd(false);
            setEditingContact(null);
            if (refresh) afterMutation();
          }}
          showToast={showToast}
        />
      )}

      {importTarget && (
        <ImportModal
          group={importTarget}
          onClose={(refresh) => { onCloseImport(refresh); if (refresh) afterMutation(); }}
          showToast={showToast}
        />
      )}

      <BulkConfirmModal
        pending={confirmBulk}
        selectedCount={selected.size}
        groups={allGroups.filter((g) => g._id !== group._id)}
        busy={bulkBusy}
        onCancel={() => !bulkBusy && setConfirmBulk(null)}
        onConfirm={runBulk}
      />
    </div>
  );
};

/* ── Bulk action confirm ────────────────────────────────────────────────── */
const BulkConfirmModal = ({ pending, selectedCount, groups, busy, onCancel, onConfirm }) => {
  const [targetGroupId, setTargetGroupId] = useState("");
  useEffect(() => { setTargetGroupId(""); }, [pending]);

  if (!pending) return null;
  const isCopy = pending.action === "copy";

  const COPY = {
    unsubscribe: {
      title: "Unsubscribe contacts",
      body: "They will be excluded from every future send, in every group — not just this one.",
      variant: "warning", confirm: "Unsubscribe",
    },
    reactivate: { title: "Reactivate contacts", body: "They will start receiving messages again.", variant: "success", confirm: "Reactivate" },
    copy: { title: "Copy to another group", body: "The contacts stay in this group as well.", variant: "info", confirm: "Copy" },
    remove_from_group: {
      title: "Remove from this group",
      body: "The contacts are not deleted — they stay in any other group they belong to.",
      variant: "warning", confirm: "Remove",
    },
    delete: {
      title: "Delete contacts",
      body: "Deleted everywhere, from every group. This cannot be undone.",
      variant: "danger", confirm: "Delete",
    },
  }[pending.action];

  return (
    <ConfirmModal
      show
      onHide={onCancel}
      onConfirm={() => onConfirm({
        action: isCopy ? "add_to_group" : pending.action,
        targetGroupId: isCopy ? targetGroupId : undefined,
      })}
      title={COPY.title}
      variant={COPY.variant}
      confirmText={COPY.confirm}
      loading={busy}
      confirmDisabled={isCopy && !targetGroupId}
      message={
        <div>
          <p>{selectedCount} contact{selectedCount !== 1 ? "s" : ""} selected.</p>
          <p className="wa-cg-confirm__body">{COPY.body}</p>
          {isCopy && (
            <Form.Select
              size="sm" value={targetGroupId}
              onChange={(e) => setTargetGroupId(e.target.value)}
            >
              <option value="">Choose a group…</option>
              {groups.map((g) => <option key={g._id} value={g._id}>{g.name}</option>)}
            </Form.Select>
          )}
        </div>
      }
    />
  );
};

/* ── Single contact add/edit ────────────────────────────────────────────── */
const ContactFormModal = ({ contact, groupId, onClose, showToast }) => {
  const [phone, setPhone] = useState(contact?.phone || "");
  const [name, setName] = useState(contact?.name || "");
  const [course, setCourse] = useState(contact?.course || "");
  const [status, setStatus] = useState(contact?.status || "active");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (contact) {
        await apiClient.put(`/whatsapp/contacts/${contact._id}`, { name, course, status });
      } else {
        await apiClient.post("/whatsapp/contacts", { phone, name, course, groupIds: [groupId] });
      }
      showToast(contact ? "Contact updated" : "Contact added");
      onClose(true);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save contact", "error");
    }
    setSaving(false);
  };

  return (
    <Modal show centered onHide={() => !saving && onClose(false)}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
          {contact ? "Edit Contact" : "Add Contact"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Phone number <span className="wa-required">*</span></Form.Label>
          <Form.Control
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="9876543210 or 919876543210"
            // The number is the identity of the record; changing it would be a
            // different contact. Edit deletes and re-adds instead.
            disabled={!!contact}
          />
          <div className="wa-form-hint">
            Indian numbers get 91 added automatically. Include the country code for others.
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Name <span className="wa-optional">(optional)</span></Form.Label>
          <Form.Control value={name} onChange={(e) => setName(e.target.value)} maxLength={160} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Course <span className="wa-optional">(optional)</span></Form.Label>
          <Form.Control value={course} onChange={(e) => setCourse(e.target.value)} maxLength={160} />
        </Form.Group>
        {contact && (
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="invalid">Invalid number</option>
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancel</AppButton>
        <AppButton onClick={save} disabled={saving || (!contact && !phone.trim())}>
          {saving ? <Loader2 size={14} className="wa-spin" /> : null} Save
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   Import modal — parse locally, upload in chunks
   ══════════════════════════════════════════════════════════════════════════ */
const ImportModal = ({ group, onClose, showToast }) => {
  const [rawText, setRawText] = useState("");
  const [fileName, setFileName] = useState("");
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total, imported, updated }
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const cancelRef = useRef(false);

  const parsed = useMemo(() => parseContactRows(rawText), [rawText]);
  const tooMany = parsed.rows.length > MAX_IMPORT_TOTAL_ROWS;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setRawText(String(reader.result || ""));
    reader.onerror = () => showToast("Could not read that file", "error");
    reader.readAsText(file);
  };

  /**
   * Upload the parsed rows a chunk at a time.
   *
   * Sequential, not parallel: concurrent chunks carrying the same number race on
   * the `(instituteId, phone)` upsert, and the surviving name becomes a coin
   * flip. Sequential also keeps the progress bar honest and means a mid-import
   * cancel leaves a knowable state — everything before the current chunk landed.
   */
  const startImport = async () => {
    if (!parsed.rows.length || tooMany) return;
    setImporting(true);
    cancelRef.current = false;
    setResult(null);

    const chunks = [];
    for (let i = 0; i < parsed.rows.length; i += IMPORT_CHUNK_SIZE) {
      chunks.push(parsed.rows.slice(i, i + IMPORT_CHUNK_SIZE));
    }

    const totals = { imported: 0, updated: 0, invalid: 0, duplicatesInFile: 0, failedChunks: 0 };
    setProgress({ done: 0, total: chunks.length, ...totals });

    for (let i = 0; i < chunks.length; i++) {
      if (cancelRef.current) break;
      try {
        const { data } = await apiClient.post("/whatsapp/contacts/import", {
          groupId: group._id,
          rows: chunks[i],
          updateExisting,
        });
        totals.imported += data.imported || 0;
        totals.updated += data.updated || 0;
        totals.invalid += data.invalid || 0;
        totals.duplicatesInFile += data.duplicatesInFile || 0;
      } catch (err) {
        // One bad chunk must not abandon the other 24. Counted and reported, so
        // the operator knows the import was partial rather than assuming it was
        // clean — re-running the same file is safe (upserts), so the fix is
        // simply to import again.
        totals.failedChunks++;
        // eslint-disable-next-line no-console
        console.warn("[contact-import] chunk failed", i, err?.response?.data?.message || err.message);
      }
      setProgress({ done: i + 1, total: chunks.length, ...totals });
    }

    setImporting(false);
    setResult({ ...totals, cancelled: cancelRef.current, skippedRows: parsed.skipped });
  };

  const close = () => {
    if (importing) { cancelRef.current = true; return; }
    onClose(!!result);
  };

  return (
    <Modal show centered size="lg" onHide={close} className="wa-cg-import-modal">
      <Modal.Header closeButton={!importing}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
          <Upload size={18} /> Import into “{group.name}”
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <div className="wa-cg-import__result">
            <div className={`wa-cg-import__result-icon ${result.failedChunks ? "warn" : "ok"}`}>
              {result.failedChunks ? <AlertTriangle size={28} /> : <CheckCircle size={28} />}
            </div>
            <h4>{result.cancelled ? "Import stopped" : "Import finished"}</h4>
            <div className="wa-cg-import__stats">
              <div><strong>{result.imported.toLocaleString()}</strong><span>new contacts</span></div>
              <div><strong>{result.updated.toLocaleString()}</strong><span>already existed</span></div>
              <div><strong>{(result.invalid + result.skippedRows).toLocaleString()}</strong><span>invalid rows</span></div>
              <div><strong>{result.duplicatesInFile.toLocaleString()}</strong><span>duplicates in file</span></div>
            </div>
            {result.failedChunks > 0 && (
              <div className="wa-alert wa-alert--warning">
                <AlertTriangle size={14} />
                {result.failedChunks} batch{result.failedChunks !== 1 ? "es" : ""} failed to upload.
                Importing the same file again is safe — numbers already saved will not be duplicated.
              </div>
            )}
          </div>
        ) : importing ? (
          <div className="wa-cg-import__progress">
            <Spinner animation="border" size="sm" />
            <div className="wa-cg-import__progress-text">
              Uploading batch {progress?.done ?? 0} of {progress?.total ?? 0}…
              <span>{(progress?.imported ?? 0).toLocaleString()} contacts added so far</span>
            </div>
            <div className="wa-cg-import__bar">
              <div
                className="wa-cg-import__bar-fill"
                style={{ width: `${progress?.total ? (progress.done / progress.total) * 100 : 0}%` }}
              />
            </div>
            <p className="wa-cg-import__hint">
              You can leave this open — closing it stops after the current batch.
            </p>
          </div>
        ) : (
          <>
            <div className="wa-cg-import__source">
              <button className="wa-cg-import__file" onClick={() => fileRef.current?.click()}>
                <FileSpreadsheet size={16} />
                {fileName || "Choose a CSV file"}
              </button>
              <input
                ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain"
                style={{ display: "none" }} onChange={onFile}
              />
              <span className="wa-cg-import__or">or paste below</span>
            </div>

            <Form.Control
              as="textarea" rows={8} className="wa-cg-import__textarea"
              value={rawText}
              onChange={(e) => { setRawText(e.target.value); setFileName(""); }}
              placeholder={"9876543210, Ramesh Kumar, Data Science\n9123456789, Priya S, Full Stack\n9988776655"}
            />

            <div className="wa-form-hint" style={{ marginTop: 6 }}>
              One contact per line: <strong>phone, name, course</strong> — name and course optional.
              A header row (<code>Phone,Name,Course</code>) is detected and used to match columns
              in any order.
            </div>

            <Form.Check
              type="switch" id="wa-cg-update-existing" className="wa-cg-import__switch"
              label="Update the name and course of numbers already saved"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
            />
            <div className="wa-form-hint">
              Off by default: a contact is shared across groups, so this would overwrite a
              name saved from another list.
            </div>

            {parsed.rows.length > 0 && (
              <div className={`wa-cg-import__summary ${tooMany ? "is-error" : ""}`}>
                <Users size={14} />
                <span>
                  <strong>{parsed.rows.length.toLocaleString()}</strong> valid number
                  {parsed.rows.length !== 1 ? "s" : ""} found
                  {parsed.skipped > 0 && ` · ${parsed.skipped.toLocaleString()} row(s) skipped as invalid`}
                  {!tooMany && parsed.rows.length > IMPORT_CHUNK_SIZE && (
                    <> · uploads in {Math.ceil(parsed.rows.length / IMPORT_CHUNK_SIZE)} batches</>
                  )}
                </span>
              </div>
            )}
            {tooMany && (
              <div className="wa-alert wa-alert--warning">
                <AlertTriangle size={14} />
                That is over {MAX_IMPORT_TOTAL_ROWS.toLocaleString()} rows. Split the file and
                import it in parts.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <AppButton onClick={() => onClose(true)}>Done</AppButton>
        ) : (
          <>
            <AppButton variant="outline" onClick={close}>
              {importing ? "Stop after this batch" : "Cancel"}
            </AppButton>
            <AppButton onClick={startImport} disabled={importing || !parsed.rows.length || tooMany}>
              <Upload size={14} /> Import {parsed.rows.length > 0 ? parsed.rows.length.toLocaleString() : ""}
            </AppButton>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ContactGroupsTab;
export { parseContactRows, IMPORT_CHUNK_SIZE };
